// privacy.guard.ts
import { inject } from '@angular/core';
import {
  CanActivateFn,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError, timeout } from 'rxjs/operators';
import { ProjectService } from '../services/project.service';
import { UserService } from '../services/user.service';
import { AuthService } from '../services/auth.service';
import { SeoService } from '../services/seo.service';
import { getProjectTypeLabel } from '../enums/project-type.enum';
import { ProjectType } from '../enums/project-type.enum';

export const privacyGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | import('@angular/router').UrlTree> => {
  const projectService = inject(ProjectService);
  const userService = inject(UserService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const seoService = inject(SeoService);

  const idform = Number(route.paramMap.get('idform'));

  if (!idform) {
    return of(router.createUrlTree(['/']));
  }

  return projectService
    .getProjectDetail(idform, {
      includeUser: true,
      includeDescription: true,
      forceRefresh: true,
    })
    .pipe(
      timeout(5000), // Evita que SSR de Vercel haga timeout (10s) si Render está en cold start
      map((project) => {
        // Usar optimizedThumbnail ya mapeado por ProjectService.mapProjectImages()
        // Si no tiene imagen, usar la imagen por defecto de la plataforma (URL absoluta)
        const projectUrl = `https://www.myplacetoday.com/games/view-form/${idform}/${project.slug || ''}`;
        const ogImage = project.optimizedThumbnail?.startsWith('http')
          ? project.optimizedThumbnail
          : 'https://www.myplacetoday.com/MyPlace.png';

        const gameType = getProjectTypeLabel(
          project.tipoform as unknown as ProjectType,
        );
        const seoTitle = `${project.nameform} - Jugar ${gameType}`;
        const seoDescription =
          project.descripcion ||
          `Juega gratis a la ${gameType} de ${project.nameform} en MyPlace (myplacetoday). ¡Participa, vota y crea tus propios juegos interactivos!`;

        // Actualizar SEO sincrónicamente antes de que Angular termine de rutear y Vercel serialice
        seoService.updateMeta({
          title: seoTitle,
          description: seoDescription,
          image: ogImage,
          url: projectUrl,
        });

        // JSON-LD también en el guard para que esté disponible en el HTML SSR
        seoService.setJsonLd({
          '@context': 'https://schema.org',
          '@type': 'Game',
          name: project.nameform,
          description: seoDescription,
          image: ogImage,
          url: projectUrl,
          genre: 'Interactive Game',
          author: {
            '@type': 'Person',
            name: project.user?.username || 'Comunidad MyPlace',
          },
        });

        if (project.is_public) return true;

        const currentUserId = userService.getUserId();
        const ownerId = project.user?.id ?? (project as any).user_id;

        if (currentUserId && String(currentUserId) === String(ownerId)) {
          return true;
        }

        if (!authService.isLoggedIn()) {
          return router.createUrlTree(['/login'], {
            queryParams: { returnUrl: state.url },
          });
        }

        return router.createUrlTree(['/']);
      }),
      catchError((err) => {
        if (err?.status === 403 || err?.status === 404) {
          if (!authService.isLoggedIn()) {
            return of(
              router.createUrlTree(['/login'], {
                queryParams: { returnUrl: state.url },
              }),
            );
          }
          return of(router.createUrlTree(['/']));
        }
        return of(router.createUrlTree(['/']));
      }),
    );
};
